import React, { useState, useEffect, useCallback, useRef } from 'react';
import {
  FaMapPin,
  FaCheckCircle,
  FaCamera,
  FaComment,
  FaTimes,
  FaSpinner,
  FaSync,
  FaUser,
  FaClock,
  FaTrash,
  FaImage,
  FaExclamationTriangle,
  FaLock,
  FaCrown
} from 'react-icons/fa';
import { getCheckins, createCheckin, deleteCheckin, getCheckinPhoto } from '../api/client';
import { extractLocationsByDay, structuredToDays } from '../utils/itineraryParser';
import './TripTimeline.css';

// Subscription tiers that have access to timeline feature
const TIMELINE_ALLOWED_TIERS = ['individual', 'group', 'starter_agent', 'professional_agent', 'enterprise_agency', 'promotional', 'unlimited', 'admin'];

const TripTimeline = ({ requestId, itineraryContent, structuredItinerary = null, isOwner = false, onViewPricing }) => {
  const [checkins, setCheckins] = useState([]);
  const [checkinsByDay, setCheckinsByDay] = useState({});
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [refreshing, setRefreshing] = useState(false);
  const [actionMessage, setActionMessage] = useState(null); // { type: 'error' | 'warning' | 'success', text: string }

  // Check-in modal state
  const [showCheckinModal, setShowCheckinModal] = useState(false);
  const [selectedStop, setSelectedStop] = useState(null);
  const [checkinNote, setCheckinNote] = useState('');
  const [checkinPhoto, setCheckinPhoto] = useState(null);
  const [checkinPhotoPreview, setCheckinPhotoPreview] = useState(null);
  const [submitting, setSubmitting] = useState(false);

  // Photo viewer modal state
  const [showPhotoModal, setShowPhotoModal] = useState(false);
  const [viewingPhotoUrl, setViewingPhotoUrl] = useState(null);
  const [loadingPhoto, setLoadingPhoto] = useState(false);

  const fileInputRef = useRef(null);
  const isMountedRef = useRef(true);
  const actionMessageTimeoutRef = useRef(null);

  // Helper to show action messages
  const showActionMessage = useCallback((type, text, duration = 4000) => {
    setActionMessage({ type, text });
    if (actionMessageTimeoutRef.current) {
      clearTimeout(actionMessageTimeoutRef.current);
    }
    actionMessageTimeoutRef.current = setTimeout(() => {
      setActionMessage(null);
    }, duration);
  }, []);

  // Cleanup on unmount to prevent state updates after navigation
  useEffect(() => {
    isMountedRef.current = true;
    return () => {
      isMountedRef.current = false;
    };
  }, []);

  // Get current user ID from localStorage
  const currentUser = JSON.parse(localStorage.getItem('user') || '{}');
  const currentUserId = currentUser.id;
  const userTier = currentUser.subscription_tier || 'demo';
  const hasTimelineAccess = TIMELINE_ALLOWED_TIERS.includes(userTier);

  // Extract stops from itinerary — prefer the authoritative server-parsed
  // structured document (#2); fall back to client-side Markdown parsing.
  const locationsByDay = structuredToDays(structuredItinerary) || extractLocationsByDay(itineraryContent);

  // Fetch check-ins
  const fetchCheckins = useCallback(async (showRefreshing = false) => {
    if (showRefreshing) setRefreshing(true);
    try {
      const data = await getCheckins(requestId);
      // Only update state if component is still mounted
      if (!isMountedRef.current) return;
      setCheckins(data.checkins || []);
      setCheckinsByDay(data.by_day || {});
      setError(null);
    } catch (err) {
      // Only update state if component is still mounted
      if (!isMountedRef.current) return;
      console.error('Error fetching check-ins:', err);
      if (err.response?.status === 403) {
        setError('You don\'t have access to this itinerary. Ask the owner to add you as a member.');
      } else {
        setError('Failed to load check-ins');
      }
    } finally {
      if (isMountedRef.current) {
        setLoading(false);
        setRefreshing(false);
      }
    }
  }, [requestId]);

  useEffect(() => {
    fetchCheckins();
  }, [fetchCheckins]);

  // Listen for real-time check-in notifications
  useEffect(() => {
    const handleCheckinNotification = (event) => {
      const data = event.detail;
      if (data && data.type === 'trip_checkin' && data.itinerary_id === requestId) {
        // Refresh check-ins when a new one comes in
        fetchCheckins();
      }
    };

    window.addEventListener('checkin-notification', handleCheckinNotification);
    return () => {
      window.removeEventListener('checkin-notification', handleCheckinNotification);
    };
  }, [requestId, fetchCheckins]);

  // Check if current user has checked in at a specific stop
  const hasUserCheckedIn = (dayNumber, stopName) => {
    const dayCheckins = checkinsByDay[dayNumber] || [];
    return dayCheckins.some(
      c => c.user_id === currentUserId && c.stop_name === stopName
    );
  };

  // Get all check-ins for a specific stop
  const getStopCheckins = (dayNumber, stopName) => {
    const dayCheckins = checkinsByDay[dayNumber] || [];
    return dayCheckins.filter(c => c.stop_name === stopName);
  };

  // Handle check-in button click
  const handleCheckinClick = (dayNumber, stop) => {
    setSelectedStop({
      dayNumber,
      name: stop.name,
      lat: stop.lat,
      lng: stop.lng
    });
    setCheckinNote('');
    setCheckinPhoto(null);
    setCheckinPhotoPreview(null);
    setShowCheckinModal(true);
  };

  // Handle photo selection
  const handlePhotoSelect = (e) => {
    const file = e.target.files[0];
    if (file) {
      if (file.size > 5 * 1024 * 1024) {
        showActionMessage('warning', 'Photo must be less than 5MB');
        return;
      }
      setCheckinPhoto(file);
      // Create preview
      const reader = new FileReader();
      reader.onload = (e) => setCheckinPhotoPreview(e.target.result);
      reader.readAsDataURL(file);
    }
  };

  // Submit check-in
  const handleSubmitCheckin = async () => {
    if (!selectedStop) return;

    setSubmitting(true);
    try {
      await createCheckin(
        requestId,
        {
          day_number: selectedStop.dayNumber,
          stop_name: selectedStop.name,
          latitude: selectedStop.lat,
          longitude: selectedStop.lng,
          note: checkinNote || null
        },
        checkinPhoto
      );

      setShowCheckinModal(false);
      fetchCheckins();
    } catch (err) {
      console.error('Error creating check-in:', err);
      const errorMsg = err.response?.data?.detail || 'Failed to check in. Please try again.';
      showActionMessage('error', errorMsg);
    } finally {
      setSubmitting(false);
    }
  };

  // Delete check-in
  const handleDeleteCheckin = async (checkinId) => {
    if (!window.confirm('Are you sure you want to delete this check-in?')) return;

    try {
      await deleteCheckin(requestId, checkinId);
      showActionMessage('success', 'Check-in deleted');
      fetchCheckins();
    } catch (err) {
      console.error('Error deleting check-in:', err);
      showActionMessage('error', 'Failed to delete check-in');
    }
  };

  // View photo
  const handleViewPhoto = async (checkinId) => {
    setLoadingPhoto(true);
    setShowPhotoModal(true);
    try {
      const blob = await getCheckinPhoto(requestId, checkinId);
      const url = URL.createObjectURL(blob);
      setViewingPhotoUrl(url);
    } catch (err) {
      console.error('Error loading photo:', err);
      showActionMessage('error', 'Failed to load photo');
      setShowPhotoModal(false);
    } finally {
      setLoadingPhoto(false);
    }
  };

  // Close photo modal
  const closePhotoModal = () => {
    setShowPhotoModal(false);
    if (viewingPhotoUrl) {
      URL.revokeObjectURL(viewingPhotoUrl);
      setViewingPhotoUrl(null);
    }
  };

  // Format timestamp
  const formatTime = (isoString) => {
    const date = new Date(isoString);
    return date.toLocaleString('en-US', {
      month: 'short',
      day: 'numeric',
      hour: 'numeric',
      minute: '2-digit',
      hour12: true
    });
  };

  if (loading) {
    return (
      <div className="trip-timeline-loading">
        <FaSpinner className="animate-spin" />
        <span>Loading timeline...</span>
      </div>
    );
  }

  if (error) {
    return (
      <div className="trip-timeline-error">
        <FaExclamationTriangle />
        <span>{error}</span>
      </div>
    );
  }

  // Show upgrade prompt for users without timeline access
  if (!hasTimelineAccess) {
    return (
      <div className="trip-timeline">
        <div className="trip-timeline-header">
          <div className="trip-timeline-title">
            <FaMapPin className="trip-timeline-icon" />
            <h2>Trip Timeline</h2>
          </div>
        </div>

        <div className="bg-gradient-to-br from-amber-50 to-orange-50 border-2 border-amber-200 rounded-xl p-6 text-center">
          <div className="w-16 h-16 bg-gradient-to-br from-amber-400 to-orange-500 rounded-full flex items-center justify-center mx-auto mb-4 shadow-lg">
            <FaLock className="text-white text-2xl" />
          </div>
          <h3 className="text-xl font-bold text-neutral-800 mb-2">Timeline is a Premium Feature</h3>
          <p className="text-neutral-600 mb-4">
            Check in at stops, share photos, and track your journey with group members.
            This feature is available with our premium plans.
          </p>
          <div className="flex flex-wrap justify-center gap-3 mb-4">
            <div className="flex items-center gap-2 text-sm text-neutral-600">
              <FaCheckCircle className="text-green-500" />
              <span>Real-time check-ins</span>
            </div>
            <div className="flex items-center gap-2 text-sm text-neutral-600">
              <FaCamera className="text-green-500" />
              <span>Photo uploads</span>
            </div>
            <div className="flex items-center gap-2 text-sm text-neutral-600">
              <FaUser className="text-green-500" />
              <span>Group sharing</span>
            </div>
          </div>
          {onViewPricing && (
            <button
              onClick={onViewPricing}
              className="inline-flex items-center gap-2 bg-gradient-to-r from-amber-500 to-orange-500 text-white px-6 py-3 rounded-xl font-semibold hover:shadow-lg transition-all"
            >
              <FaCrown />
              Upgrade to Premium
            </button>
          )}
        </div>
      </div>
    );
  }

  return (
    <div className="trip-timeline">
      {/* Header */}
      <div className="trip-timeline-header">
        <div className="trip-timeline-title">
          <FaMapPin className="trip-timeline-icon" />
          <h2>Trip Timeline</h2>
        </div>
        <button
          onClick={() => fetchCheckins(true)}
          disabled={refreshing}
          className="trip-timeline-refresh"
          title="Refresh"
        >
          <FaSync className={refreshing ? 'animate-spin' : ''} />
        </button>
      </div>

      <p className="trip-timeline-subtitle">
        Check in at stops to share your progress with the group
      </p>

      {/* Action message banner */}
      {actionMessage && (
        <div className={`trip-timeline-action-message trip-timeline-action-${actionMessage.type}`}>
          {actionMessage.type === 'error' && <FaExclamationTriangle />}
          {actionMessage.type === 'warning' && <FaExclamationTriangle />}
          {actionMessage.type === 'success' && <FaCheckCircle />}
          <span>{actionMessage.text}</span>
          <button onClick={() => setActionMessage(null)} className="trip-timeline-action-dismiss">
            <FaTimes />
          </button>
        </div>
      )}

      {/* Day sections */}
      {locationsByDay.length === 0 ? (
        <div className="trip-timeline-empty">
          <FaMapPin />
          <p>No stops found in the itinerary</p>
        </div>
      ) : (
        locationsByDay.map((day) => (
          <div key={day.day} className="trip-timeline-day">
            {/* Day header */}
            <div className="trip-timeline-day-header">
              <span className="trip-timeline-day-number">Day {day.day}</span>
              <span className="trip-timeline-day-title">{day.dayTitle}</span>
            </div>

            {/* Stops */}
            <div className="trip-timeline-stops">
              {day.locations.length === 0 ? (
                <p className="trip-timeline-no-stops">No stops identified for this day</p>
              ) : (
                day.locations.map((stop, stopIndex) => {
                  const userCheckedIn = hasUserCheckedIn(day.day, stop.name);
                  const stopCheckins = getStopCheckins(day.day, stop.name);

                  return (
                    <div key={stopIndex} className="trip-timeline-stop">
                      {/* Timeline connector */}
                      <div className="trip-timeline-connector">
                        <div className={`trip-timeline-dot ${userCheckedIn ? 'checked' : ''}`}>
                          {userCheckedIn ? <FaCheckCircle /> : <span>{stopIndex + 1}</span>}
                        </div>
                        {stopIndex < day.locations.length - 1 && (
                          <div className="trip-timeline-line"></div>
                        )}
                      </div>

                      {/* Stop content */}
                      <div className="trip-timeline-stop-content">
                        <div className="trip-timeline-stop-header">
                          <h4 className="trip-timeline-stop-name">{stop.name}</h4>
                          {!userCheckedIn && (
                            <button
                              onClick={() => handleCheckinClick(day.day, stop)}
                              className="trip-timeline-checkin-btn"
                            >
                              <FaMapPin /> Check In
                            </button>
                          )}
                        </div>

                        {/* Check-ins for this stop */}
                        {stopCheckins.length > 0 && (
                          <div className="trip-timeline-checkins">
                            {stopCheckins.map((checkin) => (
                              <div key={checkin.id} className="trip-timeline-checkin">
                                <div className="trip-timeline-checkin-user">
                                  <FaUser />
                                  <span className="trip-timeline-checkin-name">
                                    {checkin.user_name}
                                    {checkin.member_code && (
                                      <span className="trip-timeline-member-code">
                                        #{checkin.member_code}
                                      </span>
                                    )}
                                  </span>
                                  <span className="trip-timeline-checkin-time">
                                    <FaClock /> {formatTime(checkin.checked_in_at)}
                                  </span>
                                </div>

                                {checkin.note && (
                                  <p className="trip-timeline-checkin-note">
                                    <FaComment /> {checkin.note}
                                  </p>
                                )}

                                <div className="trip-timeline-checkin-actions">
                                  {checkin.has_photo && (
                                    <button
                                      onClick={() => handleViewPhoto(checkin.id)}
                                      className="trip-timeline-view-photo"
                                    >
                                      <FaImage /> View Photo
                                    </button>
                                  )}

                                  {(checkin.user_id === currentUserId || isOwner) && (
                                    <button
                                      onClick={() => handleDeleteCheckin(checkin.id)}
                                      className="trip-timeline-delete"
                                    >
                                      <FaTrash />
                                    </button>
                                  )}
                                </div>
                              </div>
                            ))}
                          </div>
                        )}
                      </div>
                    </div>
                  );
                })
              )}
            </div>
          </div>
        ))
      )}

      {/* Check-in Modal */}
      {showCheckinModal && selectedStop && (
        <div className="trip-timeline-modal-overlay" onClick={() => setShowCheckinModal(false)}>
          <div className="trip-timeline-modal" onClick={(e) => e.stopPropagation()}>
            <div className="trip-timeline-modal-header">
              <h3>Check In</h3>
              <button onClick={() => setShowCheckinModal(false)} className="trip-timeline-modal-close">
                <FaTimes />
              </button>
            </div>

            <div className="trip-timeline-modal-content">
              <div className="trip-timeline-modal-stop">
                <FaMapPin />
                <span>{selectedStop.name}</span>
              </div>

              <div className="trip-timeline-modal-field">
                <label>Add a note (optional)</label>
                <textarea
                  value={checkinNote}
                  onChange={(e) => setCheckinNote(e.target.value)}
                  placeholder="Share what you're experiencing..."
                  maxLength={500}
                  rows={3}
                />
                <span className="trip-timeline-char-count">{checkinNote.length}/500</span>
              </div>

              <div className="trip-timeline-modal-field">
                <label>Add a photo (optional)</label>
                <input
                  type="file"
                  ref={fileInputRef}
                  onChange={handlePhotoSelect}
                  accept="image/jpeg,image/png,image/gif,image/webp"
                  style={{ display: 'none' }}
                />
                {checkinPhotoPreview ? (
                  <div className="trip-timeline-photo-preview">
                    <img src={checkinPhotoPreview} alt="Preview" />
                    <button
                      onClick={() => {
                        setCheckinPhoto(null);
                        setCheckinPhotoPreview(null);
                      }}
                      className="trip-timeline-remove-photo"
                    >
                      <FaTimes />
                    </button>
                  </div>
                ) : (
                  <button
                    onClick={() => fileInputRef.current?.click()}
                    className="trip-timeline-add-photo"
                  >
                    <FaCamera /> Add Photo
                  </button>
                )}
              </div>
            </div>

            <div className="trip-timeline-modal-footer">
              <button
                onClick={() => setShowCheckinModal(false)}
                className="trip-timeline-modal-cancel"
                disabled={submitting}
              >
                Cancel
              </button>
              <button
                onClick={handleSubmitCheckin}
                className="trip-timeline-modal-submit"
                disabled={submitting}
              >
                {submitting ? (
                  <>
                    <FaSpinner className="animate-spin" /> Checking In...
                  </>
                ) : (
                  <>
                    <FaCheckCircle /> Check In
                  </>
                )}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Photo Viewer Modal */}
      {showPhotoModal && (
        <div className="trip-timeline-modal-overlay" onClick={closePhotoModal}>
          <div className="trip-timeline-photo-modal" onClick={(e) => e.stopPropagation()}>
            <button onClick={closePhotoModal} className="trip-timeline-photo-close">
              <FaTimes />
            </button>
            {loadingPhoto ? (
              <div className="trip-timeline-photo-loading">
                <FaSpinner className="animate-spin" />
              </div>
            ) : (
              viewingPhotoUrl && <img src={viewingPhotoUrl} alt="Check-in" />
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default TripTimeline;
